/**
 * Canonical interned row identity — the SDK + render-side
 * identifier that's tested for reference equality.
 *
 * Created exclusively through `RowLocalId.Registry`. The
 * registry ensures that aliased `RowKey`s (e.g., a row's UUID
 * and its lookup key) resolve to the same `RowLocalId` instance
 * — enabling reference equality for `Map<RowLocalId, V>` and
 * `Set<RowLocalId>`.
 *
 * **User-scoped Registry**: non-UUID keys (lookup, virtual,
 * system) are scoped to the current user. Keys resolved before
 * login bind to an anonymous scope and are distinct from keys
 * resolved after `setCurrentUser()`. `promoteAnonymous(userId)`
 * would claim anonymous-scoped keys for the now-identified user,
 * but is **never called in production** — the app rebuilds row
 * identities at the login scope boundary instead, so the bridge
 * is exercised only by tests (see the scope-epoch rule in the
 * row-identity-ledger spec). UUID keys (`r_<hex>`) are globally
 * unique and bypass user scoping.
 *
 * Pre-port tento/tento-lona-js/sheets API preserved:
 *   - `resolveFromRawId(string)` — cheap path for hydrate /
 *     event handlers that already hold a canonical string.
 *   - `rawId` getter on the instance — read the canonical string
 *     without going through `.key.toString()`.
 *
 * Adopted from lona-js's `RowLocalId.Registry` (the canonical
 * shape going forward). The two classes are now nominally
 * identical so cross-package consumers don't trip on private-
 * field mismatches.
 */

import type { UserId } from "./ids.ts";
import type { Option } from "../core/option.ts";
import { Tracing } from "@tento-core/tracing";
import { Uuid } from "@tento-core/uuid";

import { RowId } from "./row-id.ts";
import { RowKey } from "./row-key.ts";
import { Internals } from "../core/internals.ts";

/**
 * `RowLocalId`'s friend record (`Internals`): its constructor registers each
 * instance. `remapKey` moves the wire key; only `Registry.rekey` (the promote
 * re-key) calls it.
 */
type RowLocalIdInternals = {
  readonly remapKey: (id: RowLocalId, key: RowKey) => void;
};
const rowLocalIdInternals = new Internals<RowLocalId, RowLocalIdInternals>("RowLocalId");

/**
 * `RowLocalId.Registry`'s friend record (`Internals`): its constructor
 * registers each instance. `resolverFace` builds the resolve-only face once
 * and keeps it on the registry (`#resolver`); `RowLocalId.resolverOf` calls it.
 */
type RegistryInternals = {
  readonly resolverFace: (registry: RowLocalId.Registry) => RowLocalId.Resolver;
};
const registryInternals = new Internals<RowLocalId.Registry, RegistryInternals>("RowLocalId.Registry");

/**
 * A row's interned identity within one client, compared by reference: a
 * `RowLocalId.Registry` resolves every key of the row to the same instance.
 */
export class RowLocalId {
  // Monotonic source for `serial`. Process-local, never persisted.
  static #nextSerial = 1;

  /**
   * Process-stable, immutable identity string — assigned once at construction
   * and NEVER changed by a re-key. The wire key (`toString` / `rawId` /
   * `key`) MUTATES on an ephemeral→server promote re-key (`Registry.rekey`), so
   * render-layer caches and identity guards that must survive a promote WITHOUT
   * a rebuild MUST key off THIS, not the wire string. (Object identity IS the
   * row's identity — stores key by `Map<RowLocalId, V>` reference; `serial` is
   * its string projection for composite cache keys.) Concretely: the text-cell
   * editor identity in `column-text`'s `adoptIdentity` keys off `serial`, so
   * the in-place promote re-key no longer trips the `identityChanged` branch
   * that recreates the focused ProseMirror editor and loses the caret.
   */
  readonly serial: string = `lid${RowLocalId.#nextSerial++}`;

  // Authoritative wire key. MUTABLE only via `Registry.rekey` (the
  // ephemeral→server promote re-key): a row's stable identity is this OBJECT —
  // stores key by `Map<RowLocalId, V>` reference — so swapping the underlying
  // key on promote keeps every store / subscription / rendered cell valid while
  // outbound ops (`rawId` / `toString`) begin addressing the server id.
  #rowKey: RowKey;

  /**
   * @internal — use `registry.resolve(key)` on YOUR scope's registry. The
   * instance is frozen: its one mutable part, the wire key, is private and
   * moves only through `Registry.rekey`.
   */
  constructor(key: RowKey) {
    rowLocalIdInternals.register(this, RowLocalId.#internals);
    this.#rowKey = key;
    Object.freeze(this);
  }

  get key(): RowKey {
    return this.#rowKey;
  }

  static readonly #internals: RowLocalIdInternals = {
    remapKey: (id, key) => {
      id.#rowKey = key;
    },
  };

  /** Canonical wire-format string. Pre-port rows-side accessor. */
  get rawId(): string {
    return this.#rowKey.toString();
  }

  toString(): string {
    return this.#rowKey.toString();
  }

  toJSON(): string {
    return this.#rowKey.toJSON();
  }
}

/**
 * Preset marker for the `virtual(r_<hex>|renderer)` ids the hydrator
 * mints for a source's auto-hydrated default renderer child. Lives here
 * (a leaf module) rather than in `variants/hydrate-default-renderer` so
 * `RendererRow` can self-identify without importing that variant-heavy
 * module — see `isSyntheticDefaultRendererId`.
 */
export const SYNTHETIC_PRESET = "renderer";

/**
 * True for the `virtual(r_<hex>|renderer)` ids the hydrator mints for
 * auto-hydrated renderer children. `RawSheet.serialize` uses this to
 * skip rows that are re-synthesized on re-hydrate — their state (unit,
 * color, …) lives on the source's attribute bag; `RendererRow` uses it
 * to project as a hidden default renderer. Re-exported from
 * `variants/hydrate-default-renderer` for back-compat.
 */
export function isSyntheticDefaultRendererId(id: RowLocalId): boolean {
  const v = id.key.rowId.asVirtual();
  if (v === null || v === undefined) return false;
  return v.preset === SYNTHETIC_PRESET;
}

/** Types and values that go with `RowLocalId`: `Claim`, `Resolver`, `resolverOf`, `Registry`. */
export namespace RowLocalId {
  /**
   * How a lookup key's current owner relates to a row claiming it
   * (`Registry.settle`): the owner keeps it (`join`), hands it to the
   * claimant (`take`), or the two are different owners (`conflict`).
   */
  export type Claim = "join" | "take" | "conflict";

  /**
   * A registry's read face: resolve keys and raw ids to `RowLocalId`s (interning
   * a first-seen key, as `resolve` does) and read the scope's user — no
   * `alias` / `claim` / `settle` / `rekey` / `setCurrentUser`. What a read-only
   * view (`SheetModelReads.registry`) hands out.
   */
  export type Resolver = Pick<Registry, "resolve" | "resolveFromRawId" | "get" | "currentUser">;

  /**
   * `registry`'s resolve-only face (`Resolver`): a frozen object forwarding
   * `resolve` / `resolveFromRawId` / `get` / `currentUser` to it, and nothing
   * else — `instanceof Registry` is false, and no cast reaches `alias`,
   * `claim`, `rekey` or `setCurrentUser`. Built once per registry, so every
   * door that hands the same scope out (`client.ids`,
   * `platformOf(client).ids`, a sheet's `registry`) hands out the same object.
   */
  export function resolverOf(registry: Registry): Resolver {
    return registryInternals.of(registry).resolverFace(registry);
  }

  /**
   * A registry is an IDENTITY SCOPE: it interns `RowKey`s to `RowLocalId`s,
   * and because every store keys by the resulting OBJECT
   * (`Map<RowLocalId, V>`), two registries hand the same wire key two
   * different rows.
   *
   * **One registry per `LonaSdk.Client`**, injected into every accessor it
   * builds. Non-UUID keys (lookup, virtual, system) are
   * additionally user-scoped inside a registry — keys resolved before login
   * bind to an anonymous scope and are distinct from keys resolved after
   * `setCurrentUser()`. UUID keys are globally unique and bypass user scoping.
   *
   * There is no process-global registry: hold the client's
   * (`platformOf(client).ids`) or a sheet's (`handle.model.registry`),
   * or build one (`new RowLocalId.Registry()`) for a standalone sheet or test.
   * `RowKey.resolve(registry)` takes it explicitly.
   */
  export class Registry {
    static readonly #ANONYMOUS = Symbol("anonymous");

    static readonly #internals: RegistryInternals = {
      resolverFace: (registry) =>
        registry.#resolver ??= Object.freeze({
          resolve: (key: RowKey) => registry.resolve(key),
          resolveFromRawId: (rawId: string) => registry.resolveFromRawId(rawId),
          get: (key: RowKey) => registry.get(key),
          get currentUser() {
            return registry.currentUser;
          },
        }),
    };

    /** An empty identity scope; registers its friend record (`RowLocalId.resolverOf`). */
    constructor() {
      registryInternals.register(this, Registry.#internals);
    }

    /** This registry's resolve-only face (`RowLocalId.resolverOf`), built on first use. */
    #resolver: Option<Resolver> = null;

    /**
     * Scoped key -> its row, held WEAKLY: an id stays interned (every resolve
     * of its keys answers the same object) for as long as anything holds it —
     * a registered row, a cache, a lease, a caller — and leaves once nothing
     * does, so a client that opened and closed many sheets does not keep every
     * key it ever resolved. A key is re-minted only after its old id was
     * collected, so two live ids never share a key. Read through `#lookup`.
     */
    readonly #map = new Map<string, WeakRef<RowLocalId>>();
    /** Drops a scoped key once its id was collected (if the key was not re-bound meanwhile). */
    readonly #collected = new FinalizationRegistry<string>((mapKey) => {
      if (this.#map.get(mapKey)?.deref() === undefined) this.#map.delete(mapKey);
    });

    /** The live id under `mapKey`, `null` when none (never bound, or collected). */
    #lookup(mapKey: string): Option<RowLocalId> {
      const ref = this.#map.get(mapKey);
      if (ref == null) return null;
      const id = ref.deref();
      if (id === undefined) {
        this.#map.delete(mapKey);
        return null;
      }
      return id;
    }

    /** Bind `mapKey` to `id` (a no-op when it already is). */
    #bind(mapKey: string, id: RowLocalId): void {
      if (this.#map.get(mapKey)?.deref() === id) return;
      this.#map.set(mapKey, new WeakRef(id));
      this.#collected.register(id, mapKey);
    }
    /**
     * Per scoped key: the owners a conflicting re-bind (`alias`) displaced,
     * oldest first — the rows `release` hands the key back to. A row that
     * gives up a key it no longer holds leaves its place here too.
     */
    readonly #displaced = new Map<string, RowLocalId[]>();
    #currentUserId: Option<UserId> = null;

    setCurrentUser(userId: UserId): void {
      this.#currentUserId = userId;
    }

    get currentUser(): Option<UserId> {
      return this.#currentUserId;
    }

    /** This registry's pre-principal scope, minted on first read (`ownerScope`). */
    #sessionScope: Option<string> = null;

    /**
     * The principal a sheet this client derives will belong to — the scope a
     * builtin's derived sheet id is namespaced by (`ephemeralFromCatalog`,
     * `hydrateSheetDefinition`'s `ownerScope`): the signed-in user id once
     * `setCurrentUser` ran, else this registry's own `session:<uuid>`, minted
     * once per registry (one per client). So a pre-login hydration agrees with
     * itself inside the client, and two clients — in one process or two —
     * never derive the same `spreadsheets.id`; a constant here would re-open
     * the cross-account squat the scope exists to close.
     */
    get ownerScope(): string {
      if (this.#currentUserId != null) return this.#currentUserId;
      this.#sessionScope ??= `session:${Uuid.make()}`;
      return this.#sessionScope;
    }

    /** Resolve a `RowKey` to its canonical `RowLocalId`. Creates one if first seen. */
    resolve(key: RowKey): RowLocalId {
      const mapKey = this.#scopedKey(key);
      const existing = this.#lookup(mapKey);
      if (existing) return existing;
      const id = new RowLocalId(key);
      this.#bind(mapKey, id);
      return id;
    }

    /**
     * Cheap path: resolve from a raw canonical string without
     * constructing a `RowKey` first. Pre-port rows-side
     * convenience — used heavily by hydrate + event handlers
     * that already hold the wire string.
     */
    resolveFromRawId(rawId: string): RowLocalId {
      return this.resolve(RowKey.fromRowId(RowId.of(rawId)));
    }

    /**
     * Register an alias: `from` key now resolves to the same `RowLocalId` as
     * `to`.
     *
     * **Owner-conflict detection.** Binding a lookup key
     * that already resolves to a *different* interned row is a conflicting
     * re-bind — the "double-claim" the uniqueness contract forbids. Today it
     * is only DETECTED, not rejected: a weather ALIAS parent and its
     * data-source CHILD legitimately resolve the same canonical key with
     * distinct localIds (L0 C1), so a hard throw would break production.
     * Last-write-wins semantics are preserved; the conflict surfaces as a
     * `Tracing.log("registry-alias/double-claim", …)` breadcrumb (captured by
     * `Tracing.onEntry`, visible in `logs.render`) carrying both serials and
     * the scoped key. Re-aliasing the SAME localId (the idempotent
     * `_accessKeyFor` path) is not a conflict and is silent.
     */
    alias(from: RowKey, to: RowLocalId): void {
      const mapKey = this.#scopedKey(from);
      const existing = this.#lookup(mapKey);
      if (existing && existing !== to) {
        Tracing.log("registry-alias/double-claim", {
          key: mapKey,
          existingStableId: existing.serial,
          incomingStableId: to.serial,
        });
        // Kept, so the key goes back to it when `to` gives it up (a
        // refused lookup-key change of `to` releases it).
        const waiting = (this.#displaced.get(mapKey) ?? []).filter((owner) => owner !== existing && owner !== to);
        waiting.push(existing);
        this.#displaced.set(mapKey, waiting);
      }
      this.#bind(mapKey, to);
    }

    /**
     * Release the key `from` when `owner` holds it (a row re-registered
     * under a new lookup key gives up its old one): the key no longer
     * resolves to `owner` — it goes back to the owner a conflicting re-bind
     * last displaced (`alias`) and that still waits for it, else it resolves
     * to nothing. A key another row holds is left alone (a newer claim
     * stands), and `owner` stops waiting for it.
     */
    release(from: RowKey, owner: RowLocalId): void {
      const mapKey = this.#scopedKey(from);
      const waiting = (this.#displaced.get(mapKey) ?? []).filter((id) => id !== owner);
      if (this.#lookup(mapKey) === owner) {
        const back = waiting.pop();
        if (back != null) this.#bind(mapKey, back);
        else this.#map.delete(mapKey);
      }
      if (waiting.length > 0) this.#displaced.set(mapKey, waiting);
      else this.#displaced.delete(mapKey);
    }

    /**
     * Claim a lookup key for `to` unless an EQUIVALENT row already owns it.
     *
     * One lookup key, one owner. A canonical data row (`:~weather:{lid}`) can
     * be shown by several viewer rows at once: the `:calendar` header and the
     * `:~lona:home` body each expand their own `alias.default-weather` into a
     * distinct virtual child with the same `lookupKey`, and both sheets share
     * this client-scoped registry. The alias exists so the shared ACCESS key
     * resolves back to row metadata, and any equivalent viewer answers that,
     * so a second viewer joins the first owner instead of re-binding the key
     * out from under it (the `registry-alias/double-claim` the old
     * unconditional `alias` raised on every fresh account).
     *
     * `isEquivalent(owner)` decides whether the current owner already speaks
     * for `to`. When it does not, this falls through to `alias`:
     * last-write-wins plus the tripwire. Returns the key's owner after the
     * call. `settle` is the general form (it can also hand the key over).
     */
    claim(
      from: RowKey,
      to: RowLocalId,
      isEquivalent: (owner: RowLocalId) => boolean,
    ): RowLocalId {
      return this.settle(
        from,
        to,
        (owner) => isEquivalent(owner) ? "join" : "conflict",
      );
    }

    /**
     * Bind a lookup key to `to`, letting the current owner's relation to `to`
     * decide who keeps it (`RowLocalId.Claim`):
     *
     *   - `join`: the owner already speaks for `to`'s data; it keeps the key.
     *   - `take`: `to` reads the same data and outranks the owner; the key
     *     moves to `to` silently (a handover, not a conflicting re-bind).
     *   - `conflict`: two different owners for one key; `alias` rebinds it
     *     last-write-wins and trips `registry-alias/double-claim`.
     *
     * An unowned key, or one `to` already owns, binds without consulting
     * `verdict`. Returns the key's owner after the call.
     */
    settle(
      from: RowKey,
      to: RowLocalId,
      verdict: (owner: RowLocalId) => RowLocalId.Claim,
    ): RowLocalId {
      const mapKey = this.#scopedKey(from);
      const owner = this.#lookup(mapKey);
      if (!owner || owner === to) {
        this.#bind(mapKey, to);
        return to;
      }
      switch (verdict(owner)) {
        case "join":
          return owner;
        case "take":
          this.#bind(mapKey, to);
          return to;
        case "conflict":
          this.alias(from, to);
          return to;
      }
    }

    /**
     * Promote re-key: the row previously addressed by `fromRaw` (an ephemeral
     * client-minted id) is now the server-minted `toRaw`. Re-point the SAME
     * `RowLocalId` so every store / subscription / rendered cell keyed by it
     * survives untouched: alias the server raw id to the existing local id
     * (INBOUND resolve) AND remap the local id's authoritative key (OUTBOUND
     * server ops — delete / move / flush address the server id). Returns null
     * (no-op) when `fromRaw` was never interned. This is what makes an
     * ephemeral→server promote invisible to the renderer — no rebuild needed.
     */
    rekey(fromRaw: string, toRaw: string): Option<RowLocalId> {
      const existing = this.#lookup(this.#scopedKey(RowKey.fromRowId(RowId.of(fromRaw))));
      if (!existing) return null;
      const toKey = RowKey.fromRowId(RowId.of(toRaw));
      this.#bind(this.#scopedKey(toKey), existing);
      rowLocalIdInternals.of(existing).remapKey(existing, toKey);
      return existing;
    }

    /** Look up without creating. */
    get(key: RowKey): Option<RowLocalId> {
      return this.#lookup(this.#scopedKey(key));
    }

    /**
     * Promote all anonymous-scoped keys to a specific user.
     *
     * Claims anonymous keys as belonging to the now-identified
     * user; keys already resolved under the user's scope are not
     * overwritten (the user-scoped entry wins). **Never called in
     * production** — the app rebuilds row identities at the login
     * scope boundary rather than promoting anonymous keys in place;
     * kept for the test suite and the scope-epoch design.
     */
    promoteAnonymous(userId: UserId): void {
      const anonPrefix = `${Registry.#ANONYMOUS.toString()}/`;
      const userPrefix = `${userId}/`;
      const toAdd: [string, RowLocalId][] = [];
      for (const [key, ref] of this.#map) {
        const localId = ref.deref();
        if (localId === undefined) continue;
        if (key.startsWith(anonPrefix)) {
          const userKey = userPrefix + key.slice(anonPrefix.length);
          if (this.#lookup(userKey) == null) {
            toAdd.push([userKey, localId]);
          }
        }
      }
      for (const [k, v] of toAdd) {
        this.#bind(k, v);
      }
    }

    // ── Internal ──────────────────────────────────────────────────

    #scopedKey(key: RowKey): string {
      const keyStr = key.toString();
      // UUID keys are globally unique — no user scoping needed.
      if (key.isUser()) return keyStr;
      const scope = this.#currentUserId ?? Registry.#ANONYMOUS.toString();
      return `${scope}/${keyStr}`;
    }
  }
}
